import React, { useContext, useState, useEffect } from 'react';
import { X, Plus, ChevronDown } from 'lucide-react';
import {
  DeviceDashboardDto,
  ParameterDtoPreferred,
} from '../../../services/deviceApi';
import {
  AlertFormData,
  AlertCondition,
  SimplifiedCreateAlertRequest,
} from '../../../types/alerts';
import { useAlertRecipients } from '../../../hooks/useAlertRecipients';
import { DevicesContext } from '../../../contexts/DevicesContext';
import { validateEmail as runEmailValidation } from '../../../utils/emailValidation';
import '../../../styles/pages/alerts.css';

interface CreateAlertModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (_alert: SimplifiedCreateAlertRequest) => void;
}

const CreateAlertModal: React.FC<CreateAlertModalProps> = ({
  isOpen,
  onClose,
  onCreate,
}) => {
  const [formData, setFormData] = useState<AlertFormData>({
    alertName: '',
    deviceName: '',
    recipients: [],
    conditions: [
      {
        type: '',
        parameter: '',
        parameterId: 0,
        operator: '>=',
        value: 0,
        unitId: 0,
      },
    ],
  });

  const [showDeviceDropdown, setShowDeviceDropdown] = useState(false);
  const [showRecipientsDropdown, setShowRecipientsDropdown] = useState(false);
  const [parametersForDevice, setParametersForDevice] = useState<
    ParameterDtoPreferred[]
  >([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Validation states
  const [validationErrors, setValidationErrors] = useState<{
    alertName?: string;
    deviceId?: string;
    recipients?: string;
    conditions?: string;
  }>({});

  // Individual condition validation errors
  const [conditionErrors, setConditionErrors] = useState<{
    [key: number]: string | undefined;
  }>({});

  // Get user devices from API
  const {
    devicesForDashboard,
    getDevicesForDashboard,
    parametersForDashboard,
    getParametersForDashboard,
    loading: devicesLoading,
    error: devicesError,
  } = useContext(DevicesContext)!;

  // Get pollutant parameters from API
  // const {
  //   parameters: pollutantParameters,
  //   loading: parametersLoading,
  //   error: parametersError,
  // } = usePollutantParameters();

  useEffect(() => {
    if (devicesForDashboard.length === 0) {
      getDevicesForDashboard();
    }
    if (parametersForDashboard.length === 0) {
      getParametersForDashboard();
    }
  }, []);

  useEffect(() => {
    if (formData.deviceId && devicesForDashboard.length > 0 && parametersForDashboard.length > 0) {
      const device = devicesForDashboard.find(
        device => device.deviceId === formData.deviceId
      );
      if (device && device.parameters) {
        const deviceParamIds = Object.keys(device.parameters);
        setParametersForDevice(
          parametersForDashboard.filter(parameter =>
            deviceParamIds.includes(parameter.driverID.toString())
          )
        );
      } else {
        // If device not found or no parameters, clear the list
        setParametersForDevice([]);
      }
    } else if (!formData.deviceId) {
      // Clear parameters when no device is selected
      setParametersForDevice([]);
    }
  }, [formData.deviceId, devicesForDashboard, parametersForDashboard]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  // Get alert recipients from API
  const {
    recipients: apiRecipients,
    loading: recipientsLoading,
    error: recipientsError,
    addRecipient,
    removeRecipient,
  } = useAlertRecipients();

  // Debug logging for recipients
  console.log('CreateAlertModal - API recipients:', apiRecipients);

  // State for new recipient input
  const [newRecipientEmail, setNewRecipientEmail] = useState('');
  const [showAddRecipientInput, setShowAddRecipientInput] = useState(false);
  const [mobileParamPickerIndex, setMobileParamPickerIndex] = useState<number | null>(null);
  const [mobileOperatorPickerIndex, setMobileOperatorPickerIndex] = useState<number | null>(null);

  // Frontend validation functions.
  // Delegates to the shared validator so we get RFC-compliant checks, fake
  // local-part rejection, disposable-domain blocking, and typo detection
  // (e.g. "mgial.com" -> "gmail.com").
  const validateEmail = (email: string) =>
    runEmailValidation(email, { blockDisposable: true, checkTypo: true });

  // Reusable function to check for duplicate conditions
  const checkDuplicateConditions = (
    conditions: AlertCondition[]
  ): { [key: number]: string | undefined } => {
    const conditionErrors: { [key: number]: string | undefined } = {};
    const conditionCombinations = new Map<string, number[]>();

    conditions.forEach((condition, idx) => {
      if (
        condition.parameter &&
        condition.operator &&
        condition.parameterId > 0
      ) {
        const combination = `${condition.parameterId}-${condition.operator}`;
        if (!conditionCombinations.has(combination)) {
          conditionCombinations.set(combination, []);
        }
        conditionCombinations.get(combination)!.push(idx);
      }
    });

    // Mark duplicate conditions
    conditionCombinations.forEach(indices => {
      if (indices.length > 1) {
        indices.forEach(idx => {
          conditionErrors[idx] =
            'This parameter and operator combination is already used';
        });
      }
    });

    return conditionErrors;
  };

  const validateForm = (): boolean => {
    const errors: typeof validationErrors = {};

    // Validate alert name
    if (!formData.alertName.trim()) {
      errors.alertName = 'Alert name is required';
    } else if (formData.alertName.trim().length < 3) {
      errors.alertName = 'Alert name must be at least 3 characters long';
    } else if (formData.alertName.trim().length > 100) {
      errors.alertName = 'Alert name must be less than 100 characters';
    }

    // Validate device selection
    if (!formData.deviceId) {
      errors.deviceId = 'Please select a device';
    }

    // Validate recipients
    if (formData.recipients.length === 0) {
      errors.recipients = 'At least one recipient is required';
    } else {
      const invalidEmails = formData.recipients.filter(
        email => !validateEmail(email).ok
      );
      if (invalidEmails.length > 0) {
        errors.recipients = `Invalid email format: ${invalidEmails.join(', ')}`;
      }
    }

    // Validate conditions
    if (formData.conditions.length === 0) {
      errors.conditions = 'At least one condition is required';
    } else {
      const newConditionErrors: { [key: number]: string | undefined } = {};

      // Check for duplicate conditions
      const duplicateErrors = checkDuplicateConditions(formData.conditions);
      Object.assign(newConditionErrors, duplicateErrors);

      formData.conditions.forEach((condition, index) => {
        if (!condition.parameter || condition.parameterId === 0) {
          newConditionErrors[index] = 'Please select a parameter';
        } else if (!condition.operator) {
          newConditionErrors[index] = 'Please select an operator';
        } else if (condition.value === undefined || condition.value === null) {
          newConditionErrors[index] = 'Please enter a value';
        }
      });

      setConditionErrors(newConditionErrors);

      const invalidConditions = formData.conditions.filter(condition => {
        const isInvalid =
          !condition.parameter ||
          !condition.operator ||
          condition.value === undefined ||
          condition.value === null ||
          condition.parameterId === 0;
        if (isInvalid) {
          console.log('Invalid condition found:', {
            parameter: condition.parameter,
            operator: condition.operator,
            value: condition.value,
            parameterId: condition.parameterId,
            parameterMissing: !condition.parameter,
            operatorMissing: !condition.operator,
            valueMissing:
              condition.value === undefined || condition.value === null,
            parameterIdMissing: condition.parameterId === 0,
          });
        }
        return isInvalid;
      });

      console.log('Invalid conditions:', invalidConditions);

      if (invalidConditions.length > 0) {
        errors.conditions = 'Please complete all condition fields';
      }

      // Check if there are any duplicate conditions
      const hasDuplicates = Object.keys(duplicateErrors).length > 0;
      if (hasDuplicates) {
        errors.conditions =
          'Duplicate conditions are not allowed. Each parameter and operator combination can only be used once.';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Convert to simplified API format
    const simplifiedAlertData: SimplifiedCreateAlertRequest = {
      alertName: formData.alertName.trim(),
      deviceId: formData.deviceId!,
      conditions: formData.conditions.map(condition => {
        return {
          parameter: condition.parameter,
          operator: condition.operator,
          value: condition.value,
          pollutantParameterId: condition.parameterId,
          parameterUnitId: condition.unitId,
        };
      }),
      recipients: formData.recipients.map(email => ({
        email: email.trim(),
        name: email.split('@')[0], // Use email prefix as name
      })),
    };

    // Additional validation before submission
    const invalidConditions = simplifiedAlertData.conditions.filter(
      c => !c.pollutantParameterId || c.pollutantParameterId <= 0
    );
    if (invalidConditions.length > 0) {
      setValidationErrors(prev => ({
        ...prev,
        conditions:
          'Some conditions have invalid parameter IDs. Please reselect the parameters.',
      }));
      return;
    }
    onCreate(simplifiedAlertData);
  };

  const handleAddCondition = () => {
    const newCondition: AlertCondition = {
      type: '',
      parameter: '',
      parameterId: 0, // Initialize with 0 to indicate no selection
      operator: '>=',
      value: 0,
      unitId: 0,
    };

    setFormData({
      ...formData,
      conditions: [...formData.conditions, newCondition],
    });

    // Clear validation error when user adds a condition
    if (validationErrors.conditions) {
      setValidationErrors(prev => ({ ...prev, conditions: undefined }));
    }
  };

  const handleConditionChange = (
    index: number,
    field: keyof AlertCondition,
    value: string | number
  ) => {
    console.log('handleConditionChange called:', { index, field, value });

    const updatedConditions = [...formData.conditions];
    updatedConditions[index] = {
      ...updatedConditions[index],
      [field]: value,
    };

    console.log('Updated condition:', updatedConditions[index]);

    setFormData({
      ...formData,
      conditions: updatedConditions,
    });

    // Only check for duplicates if the condition has both parameter and operator
    const currentCondition = updatedConditions[index];
    if (
      currentCondition.parameter &&
      currentCondition.operator &&
      currentCondition.parameterId > 0
    ) {
      const duplicateErrors = checkDuplicateConditions(updatedConditions);
      setConditionErrors(duplicateErrors);
    } else {
      // Clear duplicate error for this condition if it's incomplete
      const newConditionErrors = { ...conditionErrors };
      if (
        newConditionErrors[index] ===
        'This parameter and operator combination is already used'
      ) {
        delete newConditionErrors[index];
        setConditionErrors(newConditionErrors);
      }
    }

    // Clear validation error when user modifies conditions
    if (validationErrors.conditions) {
      setValidationErrors(prev => ({ ...prev, conditions: undefined }));
    }
  };

  const handleRemoveCondition = (index: number) => {
    setFormData({
      ...formData,
      conditions: formData.conditions.filter((_, i) => i !== index),
    });
    const newConditionErrors = { ...conditionErrors };
    delete newConditionErrors[index];
    setConditionErrors(newConditionErrors);
  };

  const handleAddRecipient = (email: string) => {
    const isDuplicate = formData.recipients.some(
      r => r.toLowerCase() === email.toLowerCase()
    );
    if (isDuplicate) {
      setValidationErrors(prev => ({
        ...prev,
        recipients: 'This email is already added',
      }));
      return;
    }
    setFormData({
      ...formData,
      recipients: [...formData.recipients, email],
    });
    addRecipient(email);
    setShowRecipientsDropdown(false);
    if (validationErrors.recipients) {
      setValidationErrors(prev => ({ ...prev, recipients: undefined }));
    }
  };

  const handleRemoveRecipient = (email: string) => {
    setFormData({
      ...formData,
      recipients: formData.recipients.filter(r => r !== email),
    });
    removeRecipient(email);
  };

  const handleAddNewRecipient = () => {
    const email = newRecipientEmail.trim();
    if (!email) return;

    const result = validateEmail(email);
    if (!result.ok) {
      setValidationErrors(prev => ({
        ...prev,
        recipients: result.error || 'Please enter a valid email address',
      }));
      return;
    }
    if (result.suggestion) {
      setValidationErrors(prev => ({
        ...prev,
        recipients: `Did you mean ${result.suggestion}?`,
      }));
      return;
    }

    // Duplicate check is case-insensitive so "User@x.com" and "user@x.com"
    // are treated as the same recipient.
    const isDuplicate = formData.recipients.some(
      r => r.toLowerCase() === email.toLowerCase()
    );
    if (isDuplicate) {
      setValidationErrors(prev => ({
        ...prev,
        recipients: 'This email is already added',
      }));
      return;
    }

    setFormData({
      ...formData,
      recipients: [...formData.recipients, email],
    });
    addRecipient(email);
    setNewRecipientEmail('');
    setShowAddRecipientInput(false);
    if (validationErrors.recipients) {
      setValidationErrors(prev => ({ ...prev, recipients: undefined }));
    }
  };

  const handleAlertNameChange = (value: string) => {
    setFormData({ ...formData, alertName: value });
    const trimmed = value.trim();
    if (!trimmed) {
      setValidationErrors(prev => ({ ...prev, alertName: 'Alert name is required' }));
    } else if (trimmed.length < 4) {
      setValidationErrors(prev => ({ ...prev, alertName: 'Alert name must be at least 4 characters' }));
    } else if (value.length > 30) {
      setValidationErrors(prev => ({ ...prev, alertName: 'Alert name cannot exceed 30 characters' }));
    } else if (!/^[a-zA-Z0-9\s]+$/.test(value)) {
      setValidationErrors(prev => ({ ...prev, alertName: 'Alert name can only contain letters, numbers and spaces' }));
    } else if (!/[a-zA-Z]/.test(trimmed)) {
      setValidationErrors(prev => ({ ...prev, alertName: 'Alert name must contain at least one letter' }));
    } else if (/^(.)\1+$/.test(trimmed)) {
      setValidationErrors(prev => ({ ...prev, alertName: 'Enter a valid alert name' }));
    } else if (/([a-zA-Z])\1{2,}/.test(value)) {
      setValidationErrors(prev => ({ ...prev, alertName: 'Alert name cannot have a character repeated more than twice in a row' }));
    } else if (/[^aeiou0-9\s]{5,}/i.test(trimmed)) {
      setValidationErrors(prev => ({ ...prev, alertName: 'Enter a valid alert name' }));
    } else {
      setValidationErrors(prev => ({ ...prev, alertName: undefined }));
    }
  };

  const handleDeviceSelect = (deviceId: number, deviceName: string) => {
    console.log('CreateAlertModal - Device selected:', {
      deviceId,
      deviceName,
    });
    console.log('CreateAlertModal - Device ID type:', typeof deviceId);
    console.log('CreateAlertModal - Device ID value:', deviceId);

    setFormData({ ...formData, deviceName, deviceId });
    setShowDeviceDropdown(false);

    // Clear validation error when user selects a device
    if (validationErrors.deviceId) {
      setValidationErrors(prev => ({ ...prev, deviceId: undefined }));
    }

    // Manually trigger recipients fetch for the selected device
    if (deviceId) {
      console.log(
        'CreateAlertModal - Triggering recipients fetch for device:',
        deviceId
      );
    }
  };

  const handleDeviceDropdownClick = () => {
    setShowDeviceDropdown(!showDeviceDropdown);
    setShowRecipientsDropdown(false);
  };

  const handleRecipientsDropdownClick = () => {
    setShowRecipientsDropdown(!showRecipientsDropdown);
    setShowDeviceDropdown(false);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay zoomOutLevel">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="flex md:items-start items-center md:flex-col flex-row">
            <button onClick={onClose} className="md:hidden block mr-3">
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                className="back-icon"
              >
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
              </svg>
            </button>
            <h2 className="modal-title">Create Threshold Alert</h2>
            <p className="modal-subtitle md:block hidden">
              Set up a new alert rule with conditions and recipients.
            </p>
          </div>
          <button onClick={onClose} className="modal-close">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form className="modal-form">
          {/* Alert Name */}
          <div className="form-group">
            <label className="form-label">Alert Name</label>
            <input
              type="text"
              value={formData.alertName}
              onChange={e => handleAlertNameChange(e.target.value)}
              placeholder="Enter Alert Name"
              className={`form-input ${validationErrors.alertName ? 'border-red-500' : ''}`}
              required
            />
            {validationErrors.alertName && (
              <p className="text-red-600 text-sm mt-1">
                {validationErrors.alertName}
              </p>
            )}
          </div>

          {/* Device */}
          <div className="form-group">
            <label className="form-label">Device</label>
            <div className="relative">
              <button
                type="button"
                onClick={handleDeviceDropdownClick}
                className={`${formData.deviceName && 'text-[var(--color-text)]'} form-dropdown-button  ${showDeviceDropdown && 'active'} ${validationErrors.deviceId ? 'border-red-500' : ''}`}
                disabled={devicesLoading && devicesForDashboard.length === 0}
              >
                {devicesLoading && devicesForDashboard.length === 0
                  ? 'Loading devices...'
                  : formData.deviceName || 'Select Device'}
                <ChevronDown className="w-5 h-5 text-[var(--color-text)]" />
              </button>
              
                  {showDeviceDropdown && !(devicesLoading && devicesForDashboard.length === 0) && (
                    <>
                    {!isMobile ? (
                      <div className="dropdown-menu">
                        <div
                          className="dropdown-item"
                          style={{ pointerEvents: 'none' }}
                        >
                          Select Device
                        </div>
                        {devicesError ? (
                          <div className="dropdown-item text-red-600">
                            Error loading devices: {devicesError}
                          </div>
                        ) : devicesForDashboard.length === 0 ? (
                          <div className="dropdown-item text-gray-500">
                            No devices found
                          </div>
                        ) : (
                          devicesForDashboard.map(
                            (device: DeviceDashboardDto) => (
                              <button
                                key={device.deviceId}
                                type="button"
                                onClick={() =>
                                  handleDeviceSelect(
                                    device.deviceId,
                                    device.deviceName || ''
                                  )
                                }
                                className={`dropdown-item ${formData.deviceId === device.deviceId ? 'selected' : ''}`}
                              >
                                {device.deviceName}
                              </button>
                            )
                          )
                        )}
                      </div>
                     ) : (
                        <div
                          className="mobile-modal-overlay"
                          onClick={() => handleDeviceDropdownClick()}
                        >
                          <div
                            className="mobile-modal"
                            onClick={e => e.stopPropagation()}
                          >
                            <div className="mobile-modal-header">
                              <h3 className="mobile-modal-title">Select Device</h3>
                              <X className="w-5 h-5 text-[var(--color-text)]" onClick={() => handleDeviceDropdownClick()} />
                            </div>
                            <div className="mobile-modal-content">
                              {devicesForDashboard.map(device => (
                                <label
                                  key={device.deviceId}
                                  className="mobile-modal-option"
                                >
                                  <input
                                    type="radio"
                                    name="device"
                                    value={device.deviceId}
                                    // checked={selectedDeviceId === device.deviceId}
                                    checked={formData.deviceId === device.deviceId}
                                    onChange={() =>
                                      handleDeviceSelect(
                                        device.deviceId,
                                        device.deviceName || ''
                                      )
                                    }
                                    className="mobile-modal-radio"
                                  />
                                  <span className="mobile-modal-label">
                                    {device.deviceName}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </div>
                        </div>
                    
                  )}
                </>
              
              )}
            </div>
            {validationErrors.deviceId && (
              <p className="text-red-600 text-sm mt-1">
                {validationErrors.deviceId}
              </p>
            )}
            {devicesError && (
              <p className="text-red-600 text-sm mt-1">{devicesError}</p>
            )}
          </div>

          {/* Recipients */}
          <div className="form-group">
            <label className="form-label">Recipients</label>
            <div className="relative" style={{ zIndex: showRecipientsDropdown ? 10 : 'auto' }}>
              <button
                type="button"
                onClick={handleRecipientsDropdownClick}
                className={`${formData.recipients.length > 0 && 'text-[var(--color-text)]'} form-dropdown-button ${showRecipientsDropdown && 'active'} ${validationErrors.recipients ? 'border-red-500' : ''}`}
              >
                {formData.recipients.length > 0
                  ? `${formData.recipients.length} selected`
                  : 'Select Email'}
                <ChevronDown className="w-5 h-5 text-[var(--color-text)]" />
              </button>
              {showRecipientsDropdown && (
                <div className="dropdown-menu" style={{ position: 'absolute', zIndex: 20, maxHeight: showAddRecipientInput ? '280px' : '200px', overflowY: 'auto' }}>
                  {recipientsLoading ? (
                    <div className="dropdown-item text-gray-500">
                      Loading recipients...
                    </div>
                  ) : recipientsError && !apiRecipients.length ? (
                    <div className="dropdown-item text-red-600">
                      Error loading recipients
                    </div>
                  ) : (
                    <>
                      {/* Show global recipients if any */}
                      {apiRecipients.length > 0 && (
                        <>
                          {apiRecipients.map(recipient => (
                            <button
                              key={recipient.alertRecipientId}
                              type="button"
                              onClick={() =>
                                handleAddRecipient(recipient.email)
                              }
                              className={`dropdown-item ${formData.recipients.includes(recipient.email) ? 'selected' : ''}`}
                            >
                              {recipient.email}{' '}
                              {recipient.name && `(${recipient.name})`}
                            </button>
                          ))}
                        </>
                      )}

                      {/* Always show Add New Recipient option */}
                      {!showAddRecipientInput ? (
                        <button
                          type="button"
                          onClick={() => setShowAddRecipientInput(true)}
                          className="dropdown-item text-[var(--color-terms-link)] hover:text-[var(--color-terms-link-hover)] font-medium"
                        >
                          + Add New Recipient
                        </button>
                      ) : (
                        <div className="p-3 border-t border-[var(--button-border-color)]">
                          <div className="text-sm font-normal text-[var(--color-text)] mb-2">
                            Add New Recipient
                          </div>
                          <div className="flex gap-2">
                            <input
                              type="email"
                              value={newRecipientEmail}
                              onChange={e => {
                                const val = e.target.value;
                                setNewRecipientEmail(val);
                                const trimmed = val.trim();
                                if (!trimmed) {
                                  setValidationErrors(prev => ({ ...prev, recipients: undefined }));
                                } else {
                                  const result = validateEmail(trimmed);
                                  if (!result.ok) {
                                    setValidationErrors(prev => ({ ...prev, recipients: result.error || 'Please enter a valid email address' }));
                                  } else {
                                    setValidationErrors(prev => ({ ...prev, recipients: undefined }));
                                  }
                                }
                              }}
                              placeholder="Enter email address"
                              className={`flex-1 text-[var(--color-text)] px-2 py-1 text-sm border ${validationErrors.recipients ? 'border-red-500' : 'border-gray-300'} rounded focus:outline-none focus:ring-1 focus:ring-[var(--color-terms-link)]`}
                              onKeyPress={e =>
                                e.key === 'Enter' && handleAddNewRecipient()
                              }
                              autoFocus
                            />
                            <button
                              type="button"
                              onClick={handleAddNewRecipient}
                              className="recipient-add-button cursor-pointer"
                              disabled={!newRecipientEmail.trim()}
                            >
                              Add
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowAddRecipientInput(false);
                                if (validationErrors.recipients) {
                                  setValidationErrors(prev => ({
                                    ...prev,
                                    recipients: undefined,
                                  }));
                                }
                              }}
                              className="recipient-cancel-button cursor-pointer"
                            >
                              Cancel
                            </button>
                          </div>
                          {validationErrors.recipients && (
                            <p style={{ color: '#ef4444', fontSize: '0.75rem', margin: '4px 0 0 0' }}>
                              {validationErrors.recipients}
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            {validationErrors.recipients && (
              <p className="text-red-600 text-sm mt-1">
                {validationErrors.recipients}
              </p>
            )}
            {formData.recipients.length > 0 && (
              <div className="recipients-tags">
                {formData.recipients.map(email => (
                  <span key={email} className="recipient-tag">
                    {email}
                    <button
                      type="button"
                      onClick={() => handleRemoveRecipient(email)}
                      className="recipient-tag-remove"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Add New Recipient Input */}
            {/* {showAddRecipientInput && (
              <div className="mt-3 p-3 border border-gray-300 rounded-lg bg-gray-50">
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={newRecipientEmail}
                    onChange={e => setNewRecipientEmail(e.target.value)}
                    placeholder="Enter email address"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    onKeyPress={e =>
                      e.key === 'Enter' && handleAddNewRecipient()
                    }
                  />
                  <button
                    type="button"
                    onClick={handleAddNewRecipient}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddRecipientInput(false);
                      setNewRecipientEmail('');
                    }}
                    className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )} */}
          </div>

          {/* Setup Conditions */}
          <div className="form-group">
            <label className="form-label">Setup Conditions</label>
            <div className="conditions-container">
              {formData.conditions.map((condition, index) => (
                <>
                  <div key={index} className="condition-row">
                    {/* Parameter Select */}
                    {isMobile ? (
                      <>
                        <button
                          type="button"
                          className="condition-select"
                          disabled={!formData.deviceId}
                          onClick={() => formData.deviceId && setMobileParamPickerIndex(index)}
                        >
                          {condition.parameter || 'Parameter'}
                          <ChevronDown className="w-4 h-4" style={{ marginLeft: 'auto', flexShrink: 0 }} />
                        </button>
                        {mobileParamPickerIndex === index && (
                          <div className="mobile-modal-overlay" onClick={() => setMobileParamPickerIndex(null)}>
                            <div className="mobile-modal" onClick={e => e.stopPropagation()}>
                              <div className="mobile-modal-header">
                                <h3 className="mobile-modal-title">Select Parameter</h3>
                                <X className="w-5 h-5 text-[var(--color-text)]" onClick={() => setMobileParamPickerIndex(null)} />
                              </div>
                              <div className="mobile-modal-content">
                                {parametersForDevice.map(parameter => {
                                  const parameterId = parameter.driverID;
                                  const label = parameter.parameterName === 'AQI'
                                    ? parameter.parameterName
                                    : `${parameter.parameterName} (${parameter.unit})`;
                                  return (
                                    <label key={parameterId} className="mobile-modal-option">
                                      <input
                                        type="radio"
                                        name={`param-${index}`}
                                        checked={condition.parameterId === parameterId}
                                        onChange={() => {
                                          const updatedConditions = [...formData.conditions];
                                          updatedConditions[index] = {
                                            ...updatedConditions[index],
                                            parameter: parameter.parameterName,
                                            parameterId,
                                            unitId: parameter.unitId,
                                          };
                                          setFormData({ ...formData, conditions: updatedConditions });
                                          if (validationErrors.conditions) {
                                            setValidationErrors(prev => ({ ...prev, conditions: undefined }));
                                          }
                                          setMobileParamPickerIndex(null);
                                        }}
                                        className="mobile-modal-radio"
                                      />
                                      <span className="mobile-modal-label">{label}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <select
                        value={
                          condition.parameterId && condition.parameterId > 0
                            ? condition.parameterId.toString()
                            : ''
                        }
                        title={condition.parameter || 'Select a parameter'}
                        onChange={e => {
                          let selectedParameterId = 0;
                          let selectedParameter: ParameterDtoPreferred | undefined;
                          if (e.target.value) {
                            const numericId = Number(e.target.value);
                            if (!isNaN(numericId)) {
                              selectedParameterId = numericId;
                              selectedParameter = parametersForDevice.find(
                                (p: ParameterDtoPreferred) => p.driverID === selectedParameterId
                              );
                            }
                          }
                          if (selectedParameter && selectedParameterId > 0) {
                            const updatedConditions = [...formData.conditions];
                            updatedConditions[index] = {
                              ...updatedConditions[index],
                              parameter: selectedParameter.parameterName,
                              parameterId: selectedParameterId,
                              unitId: selectedParameter.unitId,
                            };
                            setFormData({ ...formData, conditions: updatedConditions });
                            if (validationErrors.conditions) {
                              setValidationErrors(prev => ({ ...prev, conditions: undefined }));
                            }
                          } else {
                            const updatedConditions = [...formData.conditions];
                            updatedConditions[index] = {
                              ...updatedConditions[index],
                              parameter: '',
                              parameterId: 0,
                            };
                            setFormData({ ...formData, conditions: updatedConditions });
                            const newConditionErrors = { ...conditionErrors };
                            delete newConditionErrors[index];
                            setConditionErrors(newConditionErrors);
                          }
                        }}
                        className="condition-select"
                        required
                        disabled={!formData.deviceId}
                      >
                        <option value="">{formData.deviceId ? 'Select Parameter' : 'Select a device first'}</option>
                        {parametersForDevice.map(parameter => {
                          const parameterId = parameter.driverID;
                          return (
                            <option key={parameterId} value={parameterId.toString()}>
                              {parameter.parameterName === 'AQI'
                                ? parameter.parameterName
                                : `${parameter.parameterName}(${parameter.unit})`}
                            </option>
                          );
                        })}
                      </select>
                    )}

                    {/* Operator Select */}
                    {isMobile ? (
                      <>
                        <button
                          type="button"
                          className="condition-select condition-operator"
                          onClick={() => setMobileOperatorPickerIndex(index)}
                        >
                          {condition.operator === '>=' ? '≥' : condition.operator === '<=' ? '≤' : condition.operator === '>' ? '>' : condition.operator === '<' ? '<' : '='}
                          <ChevronDown className="w-3 h-3" style={{ flexShrink: 0 }} />
                        </button>
                        {mobileOperatorPickerIndex === index && (
                          <div className="mobile-modal-overlay" onClick={() => setMobileOperatorPickerIndex(null)}>
                            <div className="mobile-modal" onClick={e => e.stopPropagation()}>
                              <div className="mobile-modal-header">
                                <h3 className="mobile-modal-title">Select Operator</h3>
                                <X className="w-5 h-5 text-[var(--color-text)]" onClick={() => setMobileOperatorPickerIndex(null)} />
                              </div>
                              <div className="mobile-modal-content">
                                {[
                                  { value: '>=', label: '≥ Greater than or equal' },
                                  { value: '<=', label: '≤ Less than or equal' },
                                  { value: '>', label: '> Greater than' },
                                  { value: '<', label: '< Less than' },
                                  { value: '==', label: '= Equal to' },
                                ].map(op => (
                                  <label key={op.value} className="mobile-modal-option">
                                    <input
                                      type="radio"
                                      name={`operator-${index}`}
                                      checked={condition.operator === op.value}
                                      onChange={() => {
                                        handleConditionChange(index, 'operator', op.value);
                                        setMobileOperatorPickerIndex(null);
                                      }}
                                      className="mobile-modal-radio"
                                    />
                                    <span className="mobile-modal-label">{op.label}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <select
                        value={condition.operator}
                        onChange={e => handleConditionChange(index, 'operator', e.target.value)}
                        className="condition-select condition-operator"
                      >
                        <option value=">=">≥</option>
                        <option value="<=">≤</option>
                        <option value=">">&gt;</option>
                        <option value="<">&lt;</option>
                        <option value="==">=</option>
                      </select>
                    )}
                    <input
                      type="text"
                      value={condition.value || ''}
                      onChange={(e) => {
                        const inputValue = e.target.value.trim(); 
                        const regex = /^(\d+(\.\d{0,2})?)?$/; 
                        if (regex.test(inputValue) || inputValue === '') {
                          handleConditionChange(index, 'value', inputValue);
                        }
                      }}
                      className="condition-input"
                      placeholder={isMobile ? "Value" :"Enter Value"}
                      required
                    />

                    <button
                      type="button"
                      onClick={() => handleRemoveCondition(index)}
                      className="condition-remove"
                      title="Remove condition"
                    >
                      ×
                    </button>
                  </div>
                </>
              ))}
              <button
                type="button"
                onClick={handleAddCondition}
                className="add-condition-button"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {validationErrors.conditions && (
              <p className="text-red-600 text-sm mt-1">
                {validationErrors.conditions}
              </p>
            )}
          </div>
        </form>
        {/* Action Buttons */}
        <div className="modal-actions">
          <button
            type="button"
            onClick={onClose}
            className="modal-button secondary"
          >
            Cancel
          </button>
          <button onClick={handleSubmit} className="modal-button primary">
            Create
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateAlertModal;
